/**
 * Channel Partners hub — fleet KPIs, filters, sortable list, row actions.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpDown,
  Briefcase,
  ChevronDown,
  MoreHorizontal,
  RefreshCw,
  Search,
  UserPlus,
  Users,
} from 'lucide-react';
import { KpiMetricCard } from '../dashboard/KpiMetricCard';

interface ChannelPartnerRow {
  id: number;
  clientCode: string;
  companyName: string;
  partnerType: string;
  status: string;
  kybStatus: string;
  onboardingStatus: string;
  whiteLabelSlug: string | null;
  attributedUsers: number;
  kycVerified: number;
  kycVerifiedPct: number;
  registrations7d: number;
  repWelcomeEmailStatus: string | null;
}

interface FleetSummary {
  partnerCount: number;
  activePartners: number;
  attributedUsers: number;
  kycVerified: number;
  kycVerifiedPct: number;
  registrations7d: number;
}

type QuickView = '' | 'active' | 'needs_attention' | 'new_this_week';
type SortKey = 'company_name' | 'partner_id' | 'status' | 'attributed_users' | 'kyc_verified' | 'registrations_7d';

const API_BASE = '/api/v1/admin/channel-partners';
const LIMIT = 20;

const QUICK_VIEWS: { id: QuickView; label: string }[] = [
  { id: '', label: 'All partners' },
  { id: 'active', label: 'Active' },
  { id: 'needs_attention', label: 'Needs attention' },
  { id: 'new_this_week', label: 'New this week' },
];

const WELCOME_STATUS_COLORS: Record<string, string> = {
  approved: 'bg-emerald-50 text-emerald-700',
  pending_approval: 'bg-amber-50 text-amber-700',
  disabled: 'bg-red-50 text-red-700',
  draft: 'bg-gray-100 text-gray-600',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700',
  pending: 'bg-amber-50 text-amber-700',
  suspended: 'bg-red-50 text-red-700',
  closed: 'bg-gray-100 text-gray-500',
};

const KYB_STATUS_COLORS: Record<string, string> = {
  none: 'bg-gray-100 text-gray-600',
  submitted: 'bg-blue-50 text-blue-700',
  verified: 'bg-emerald-50 text-emerald-700',
  approved: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-red-50 text-red-700',
};

function authHeaders(): HeadersInit {
  const token = sessionStorage.getItem('portal_token');
  const h: Record<string, string> = { Accept: 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

export const ChannelPartnersOverlay: React.FC = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<ChannelPartnerRow[]>([]);
  const [fleetSummary, setFleetSummary] = useState<FleetSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [kybFilter, setKybFilter] = useState('');
  const [welcomeFilter, setWelcomeFilter] = useState('');
  const [quickView, setQuickView] = useState<QuickView>('');
  const [sortBy, setSortBy] = useState<SortKey>('company_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, kybFilter, welcomeFilter, quickView, sortBy, sortDir]);

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(LIMIT));
      params.set('sort_by', sortBy);
      params.set('sort_dir', sortDir);
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (statusFilter) params.set('status', statusFilter);
      if (kybFilter) params.set('kyb_status', kybFilter);
      if (welcomeFilter) params.set('welcome_email_status', welcomeFilter);
      if (quickView) params.set('quick_view', quickView);

      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (res.status === 401) {
        sessionStorage.removeItem('portal_token');
        navigate('/admin/login', { replace: true });
        return;
      }
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed to load channel partners');
      setPartners(json.data.partners || []);
      setFleetSummary(json.data.fleetSummary || null);
      setTotal(json.data.pagination?.total || 0);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load channel partners');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, statusFilter, kybFilter, welcomeFilter, quickView, sortBy, sortDir, navigate]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  useEffect(() => {
    if (openMenuId == null) return undefined;
    const close = () => setOpenMenuId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openMenuId]);

  const toggleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'company_name' || key === 'partner_id' ? 'asc' : 'desc');
    }
  };

  const SortHeader: React.FC<{ label: string; sortKey: SortKey; className?: string }> = ({
    label,
    sortKey,
    className = '',
  }) => (
    <th className={`px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap ${className}`}>
      <button
        type="button"
        onClick={() => toggleSort(sortKey)}
        className="inline-flex items-center gap-1 hover:text-gray-800"
      >
        {label}
        <ArrowUpDown className={`h-3.5 w-3.5 ${sortBy === sortKey ? 'text-[var(--primary)]' : 'opacity-40'}`} />
      </button>
    </th>
  );

  const totalPages = Math.ceil(total / LIMIT) || 1;
  const fs = fleetSummary;

  return (
    <div className="space-y-6 p-1">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="admin-text-heading text-2xl">Channel Partners</h1>
          <p className="text-sm text-gray-500 mt-1">
            Fleet oversight of partners and attributed wallet users. Register a new partner in Partner Onboarding — Partner ID and registration slug are generated from the company name.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => fetchPartners()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/partners')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-[var(--primary)] text-[var(--primary-foreground)]"
          >
            <UserPlus className="h-4 w-4" />
            New partner
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiMetricCard
          label="Channel partners"
          metrics={[{ value: String(fs?.partnerCount ?? '—') }]}
          footnote={`${fs?.activePartners ?? 0} active`}
          icon={Briefcase}
          accent="purple"
          onClick={() => { setQuickView(''); setStatusFilter(''); }}
        />
        <KpiMetricCard
          label="Attributed users"
          metrics={[{ value: (fs?.attributedUsers ?? 0).toLocaleString('en-ZA') }]}
          footnote="Across filtered fleet"
          icon={Users}
          accent="green"
          onClick={() => navigate('/admin/users')}
        />
        <KpiMetricCard
          label="KYC verified"
          metrics={[{ value: (fs?.kycVerified ?? 0).toLocaleString('en-ZA') }]}
          footnote={`${fs?.kycVerifiedPct ?? 0}% of attributed`}
          icon={Users}
          accent="blue"
          onClick={() => navigate('/admin/users')}
        />
        <KpiMetricCard
          label="New registrations (7d)"
          metrics={[{ value: (fs?.registrations7d ?? 0).toLocaleString('en-ZA') }]}
          footnote="Fleet-wide"
          icon={UserPlus}
          accent="green"
          onClick={() => setQuickView('new_this_week')}
        />
      </div>

      <div className="mymoolah-card overflow-hidden">
        <div className="p-4 border-b border-gray-100 space-y-3">
          <div className="flex flex-wrap gap-2">
            {QUICK_VIEWS.map((qv) => (
              <button
                key={qv.id || 'all'}
                type="button"
                onClick={() => setQuickView(qv.id)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
                  quickView === qv.id
                    ? 'bg-[var(--primary)]/10 border-[var(--primary)]/30 text-[var(--primary)]'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {qv.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, Partner ID, or slug…"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:ring-2 focus:ring-green-300"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700"
              aria-label="Filter by status"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
              <option value="closed">Closed</option>
            </select>
            <select
              value={kybFilter}
              onChange={(e) => setKybFilter(e.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700"
              aria-label="Filter by KYB status"
            >
              <option value="">All KYB</option>
              <option value="none">None</option>
              <option value="submitted">Submitted</option>
              <option value="verified">Verified</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              value={welcomeFilter}
              onChange={(e) => setWelcomeFilter(e.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700"
              aria-label="Filter by welcome email status"
            >
              <option value="">All welcome emails</option>
              <option value="draft">Draft</option>
              <option value="pending_approval">Pending approval</option>
              <option value="approved">Approved</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="mx-4 mt-4 p-3 rounded-xl bg-red-50 text-red-700 text-sm">{error}</div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 text-left">
                <SortHeader label="Partner" sortKey="company_name" />
                <SortHeader label="Partner ID" sortKey="partner_id" />
                <SortHeader label="Status" sortKey="status" />
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">KYB</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Welcome email</th>
                <SortHeader label="Users" sortKey="attributed_users" />
                <SortHeader label="KYC verified" sortKey="kyc_verified" />
                <SortHeader label="New (7d)" sortKey="registrations_7d" />
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-gray-400">Loading…</td>
                </tr>
              ) : partners.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-gray-400">
                    No channel partners match your filters.
                  </td>
                </tr>
              ) : (
                partners.map((p) => (
                  <tr
                    key={p.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/admin/channel-partners/${p.id}`)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-800">{p.companyName}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{p.clientCode}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-600'}`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${KYB_STATUS_COLORS[p.kybStatus] || 'bg-gray-100 text-gray-600'}`}>
                        {p.kybStatus || 'none'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${WELCOME_STATUS_COLORS[p.repWelcomeEmailStatus || ''] || 'bg-gray-100 text-gray-500'}`}>
                        {p.repWelcomeEmailStatus?.replace(/_/g, ' ') || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 tabular-nums">{p.attributedUsers}</td>
                    <td className="px-4 py-3 text-gray-700 tabular-nums">
                      {p.kycVerified}
                      <span className="text-gray-400 text-xs ml-1">({p.kycVerifiedPct}%)</span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 tabular-nums">{p.registrations7d}</td>
                    <td className="px-4 py-3 text-right relative">
                      <button
                        type="button"
                        aria-label="Partner actions"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId((cur) => (cur === p.id ? null : p.id));
                        }}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-100"
                      >
                        <MoreHorizontal className="h-4 w-4 text-gray-600" />
                      </button>
                      {openMenuId === p.id && (
                        <div
                          className="absolute right-4 top-full z-20 mt-1 w-44 rounded-xl border border-gray-200 bg-white py-1 shadow-lg text-left"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => navigate(`/admin/channel-partners/${p.id}`)}
                          >
                            <ChevronDown className="h-4 w-4 rotate-[-90deg] opacity-50" />
                            Manage
                          </button>
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => navigate(`/admin/channel-partners/${p.id}/portal/dashboard`)}
                          >
                            <ChevronDown className="h-4 w-4 rotate-[-90deg] opacity-50" />
                            Portal view
                          </button>
                          <button
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => navigate(`/admin/partners?clientId=${p.id}`)}
                          >
                            <ChevronDown className="h-4 w-4 rotate-[-90deg] opacity-50" />
                            Onboarding
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-100 flex items-center justify-between text-sm text-gray-600">
            <span>
              Page {page} of {totalPages} ({total} partners)
              {fs ? ` · ${fs.attributedUsers.toLocaleString('en-ZA')} attributed users in view` : ''}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((pg) => Math.max(1, pg - 1))}
                className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((pg) => pg + 1)}
                className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
